/**
 * apiFetch — Drop-in-Ersatz für native fetch() mit Schutz vor hängenden Requests.
 *
 * Warum nicht direkt fetch()?
 *   - Nativer fetch() hat KEIN Default-Timeout. Ein langsamer/blockierter Backend-Call
 *     friert die UI ein (z.B. ladender Spinner endlos sichtbar), weil das Promise nie
 *     resolved und `finally { setLoading(false) }` nie läuft.
 *   - Axios (services/api.js) hat 30s-Timeout, aber für SSE/Streaming-Responses
 *     (z.B. Deploy-Logs, große File-Downloads) brauchen wir die rohe Fetch-API.
 *
 * Was macht apiFetch?
 *   1. Hängt automatisch den Bearer-Token aus localStorage an (es sei denn, Caller
 *      hat schon einen Authorization-Header gesetzt).
 *   2. Prefixiert relative Pfade automatisch mit `${REACT_APP_BACKEND_URL}/api`.
 *   3. Setzt einen AbortController-Timeout (Default 30s). Mit `timeoutMs: 0` lässt
 *      sich der Timeout für Streaming-Endpoints (SSE, große Downloads) abschalten.
 *
 * Drop-in-Verwendung:
 *   // VORHER (kann hängen):
 *   const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/foo`, {
 *     headers: { Authorization: `Bearer ${token}` }
 *   });
 *
 *   // NACHHER (mit Timeout):
 *   const res = await apiFetch('/foo');
 *
 *   // Für Streaming (Deploy-Logs, große Downloads):
 *   const res = await apiFetch('/admin/update-deploy', { method: 'POST' }, { timeoutMs: 0 });
 *
 * Response-Handling ist IDENTISCH zu fetch():
 *   if (res.ok) { const data = await res.json(); ... }
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const DEFAULT_TIMEOUT_MS = 30000;

/**
 * @param {string} path - Relative API-Pfad (z.B. '/foo') ODER absolute URL.
 * @param {RequestInit} [options] - Standard fetch()-Optionen.
 * @param {{ timeoutMs?: number, skipAuth?: boolean }} [extra] - Timeout (0 = kein Timeout) und Auth-Skip.
 * @returns {Promise<Response>}
 */
export async function apiFetch(path, options = {}, extra = {}) {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, skipAuth = false } = extra;

  // Absolute URL vs. relativer Pfad
  let url;
  if (/^https?:\/\//i.test(path)) {
    url = path;
  } else if (path.startsWith('/api/')) {
    url = `${BACKEND_URL}${path}`;
  } else {
    url = `${BACKEND_URL}/api${path.startsWith('/') ? path : '/' + path}`;
  }

  // Headers zusammenbauen
  const headers = new Headers(options.headers || {});
  if (!skipAuth && !headers.has('Authorization')) {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }

  const finalOptions = { ...options, headers };

  // Wenn Caller bereits einen AbortSignal mitgibt, nicht ueberschreiben
  if (options.signal || timeoutMs === 0) {
    return fetch(url, finalOptions);
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  finalOptions.signal = controller.signal;

  try {
    return await fetch(url, finalOptions);
  } finally {
    clearTimeout(timeoutId);
  }
}

export default apiFetch;
